import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, BookOpen, Shield, Gem, Check, ChevronDown, Menu, X } from 'lucide-react';
import './LandingPage.css';

interface FeatureDetail {
    key: string;
    icon: React.ReactNode;
}

const LandingPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { isAuthenticated } = useAuth();
    const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Feature Modal State
    const [selectedFeature, setSelectedFeature] = useState<string | null>(null);

    const features: FeatureDetail[] = [
        { key: 'multiAuth', icon: <ShieldCheck size={40} /> },
        { key: 'academic', icon: <BookOpen size={40} /> },
        { key: 'rights', icon: <Shield size={40} /> },
        { key: 'pricing', icon: <Gem size={40} /> }
    ];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setLanguageDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        setLanguageDropdownOpen(false);
    };

    const currentLanguage = i18n.language || 'fr';

    const handleFeatureClick = (featureKey: string) => {
        setSelectedFeature(featureKey);
    };

    const closeFeatureModal = () => {
        setSelectedFeature(null);
    };

    return (
        <div className="landing-page">
            {/* Navigation */}
            <nav className="landing-nav">
                <div className="nav-logo">
                    <img src="/logo.svg" alt="Schoolinger Logo" width="32" height="32" />
                    <span>Schoolinger</span>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="mobile-menu-btn"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>

                {/* Mobile Menu Overlay */}
                {mobileMenuOpen && (
                    <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
                )}

                <div className={`nav-links ${mobileMenuOpen ? 'nav-links-open' : ''}`}>
                    <a href="#features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.features')}</a>
                    <a href="#contact" className="nav-link" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.contact')}</a>

                    {/* Language Switcher */}
                    <div className="language-dropdown-container" ref={dropdownRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                            className="nav-link"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                        >
                            {currentLanguage.toUpperCase()}
                            <ChevronDown size={16} />
                        </button>
                        {languageDropdownOpen && (
                            <div className="language-dropdown" style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                borderRadius: '0.5rem',
                                padding: '0.5rem',
                                minWidth: '120px',
                                zIndex: 10
                            }}>
                                <button
                                    onClick={() => changeLanguage('fr')}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '0.5rem',
                                        background: currentLanguage === 'fr' ? '#f3f4f6' : 'transparent',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Français
                                </button>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '0.5rem',
                                        background: currentLanguage === 'en' ? '#f3f4f6' : 'transparent',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    English
                                </button>
                                <button
                                    onClick={() => changeLanguage('ar')}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '0.5rem',
                                        background: currentLanguage === 'ar' ? '#f3f4f6' : 'transparent',
                                        border: 'none',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    العربية
                                </button>
                            </div>
                        )}
                    </div>

                    {isAuthenticated ? (
                        <Link to="/courses" className="btn-login" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.dashboard')}</Link>
                    ) : (
                        <Link to="/login" className="btn-login" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.login')}</Link>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <header className="hero-section">
                <h1 className="hero-title" dangerouslySetInnerHTML={{ __html: t('landing.hero.title') }} />
                <p className="hero-subtitle">
                    {t('landing.hero.subtitle')}
                </p>
                <div className="hero-cta">
                    {isAuthenticated ? (
                        <Link to="/courses" className="btn-primary">{t('landing.hero.goToDashboard')}</Link>
                    ) : (
                        <Link to="/login" className="btn-primary">{t('landing.hero.getStarted')}</Link>
                    )}
                    <a href="#features" className="btn-secondary">{t('landing.hero.learnMore')}</a>
                </div>
            </header>

            {/* Features Section */}
            <section id="features" className="features-section">
                <h2 className="section-title">{t('landing.features.title')}</h2>
                <div className="features-grid">
                    {features.map((feature) => (
                        <div
                            key={feature.key}
                            className="feature-card"
                            onClick={() => handleFeatureClick(feature.key)}
                        >
                            <div className="feature-icon">{feature.icon}</div>
                            <h3 className="feature-title">{t(`landing.features.${feature.key}.title`)}</h3>
                            <p className="feature-desc">
                                {t(`landing.features.${feature.key}.desc`)}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Feature Modal */}
            {selectedFeature && (
                <div className="modal-overlay" onClick={closeFeatureModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeFeatureModal}>&times;</button>
                        <h3 className="modal-title">{t(`landing.features.${selectedFeature}.title`)}</h3>
                        <p className="modal-desc">{t(`landing.features.${selectedFeature}.desc`)}</p>
                        <ul className="feature-list">
                            {(t(`landing.features.${selectedFeature}.details`, { returnObjects: true }) as string[]).map((detail, index) => (
                                <li key={index} className="feature-list-item">
                                    <span className="feature-list-icon"><Check size={20} /></span>
                                    {detail}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Contact Section */}
            <section id="contact" className="contact-section">
                <div className="contact-container">
                    <h2 className="contact-title">{t('landing.contact.title')}</h2>
                    <p className="contact-text">
                        {t('landing.contact.text')}
                    </p>
                    <a href="mailto:contact@solvinger.com" className="contact-btn">
                        {t('landing.contact.button')}
                    </a>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <p>&copy; {new Date().getFullYear()} Schoolinger. {t('landing.footer.rights')}</p>
            </footer>
        </div>
    );
};

export default LandingPage;
